// NOTICE PROGRESS — the Live Reporting card that was the top-nav Reports page's Notice Tracking tab (step 8 part 6,
// Philip 2026-10-22; docs/reports-step8-plan.md §5). Everything on it moved unchanged — year chips, the four KPI cards,
// the progress bar, the By Assessor / County table (nine columns, search, sort, Days Late first) and its Received /
// Waiting drill-down (opens the parcel). It gains a Client pick (all clients by default; a client group stands for its
// clients) and Excel export of the table and the drill-down list, and its year starts at the current year instead of
// a hardcoded 2026. Rendered by Live Reporting from the registry (src/reports/registry.js, `component`).
import React, { useState, useEffect, useCallback } from 'react';
import ScrollX from '../../components/ScrollX';
import { supabase } from '../../supabase';
import { useYearRange } from '../../constants/yearRange';
import { resolveFirmAssignmentsBulk } from '../../utils/resolveAssignment';
import { resolveVersionsBulk } from '../../utils/resolveVersion';
import { representedInYear } from '../../utils/retirement';
import { loadNoticeReceipts, noticeReceipt } from '../../utils/noticeReceived';
import { loadClientGroups, resolveClientIds } from '../../utils/clientGroups';
import { exportSheetsToXlsx } from '../../utils/exportXlsx';

// YEARS was ['2024','2025','2026','2027'] — a hardcoded local range the year-range consolidation missed, and
// the reason 2021 was not selectable while every current consultant assignment sits at 2021. Now
// folder_year_config via useYearRange, like every other value-year picker. See the hook call in the component.

// ── Notice mailing windows by state (commercial property, real estate) ──
// Sourced from Flanagan Bilton commercial property tax calendar.
// Used to compute "Expected By" / "Days Late" status on the Notice Tracking
// page so consultants know when to start chasing notices that haven't arrived.
const NOTICE_WINDOWS = {
  AL: { start: { m: 3, d: 15 }, end: { m: 7, d: 31 }, label: 'Late Mar – Jul' },
  AR: { start: { m: 7, d: 15 }, end: { m: 7, d: 15 }, label: 'Jul 15' },
  CO: { start: { m: 5, d: 1 },  end: { m: 5, d: 1 },  label: 'May 1' },
  DC: { start: { m: 3, d: 1 },  end: { m: 3, d: 1 },  label: 'Mar 1' },
  FL: { start: { m: 8, d: 1 },  end: { m: 8, d: 31 }, label: 'Aug (TRIM)' },
  GA: { start: { m: 4, d: 1 },  end: { m: 6, d: 30 }, label: 'Apr – Jun' },
  IA: { start: { m: 4, d: 1 },  end: { m: 4, d: 1 },  label: 'Apr 1' },
  IL: { start: { m: 5, d: 1 },  end: { m: 10, d: 31 }, label: 'May – Oct' },
  KS: { start: { m: 3, d: 1 },  end: { m: 3, d: 1 },  label: 'Mar 1' },
  KY: { start: { m: 4, d: 15 }, end: { m: 4, d: 15 }, label: 'Apr 15' },
  LA: { start: { m: 8, d: 1 },  end: { m: 9, d: 15 }, label: 'Aug – mid-Sep' },
  MD: { start: { m: 12, d: 28 }, end: { m: 1, d: 1 }, label: 'Late Dec / Jan 1', wraps_year: true },
  MS: { start: { m: 7, d: 1 },  end: { m: 7, d: 1 },  label: 'Jul 1' },
  NJ: { start: { m: 1, d: 30 }, end: { m: 1, d: 30 }, label: 'Jan 30' },
  NM: { start: { m: 3, d: 31 }, end: { m: 4, d: 1 },  label: 'By Apr 1' },
  NC: { start: { m: 1, d: 1 },  end: { m: 1, d: 1 },  label: 'Jan 1 (reappraisal yr only)' },
  NY: { start: { m: 5, d: 1 },  end: { m: 5, d: 1 },  label: 'May 1 (non-NYC) / Jan 15 (NYC)' },
  OH: { start: { m: 12, d: 23 }, end: { m: 12, d: 23 }, label: 'Dec 23' },
  OK: { start: { m: 2, d: 1 },  end: { m: 2, d: 1 },  label: 'Feb 1' },
  SC: { start: { m: 7, d: 1 },  end: { m: 7, d: 1 },  label: 'Jul 1 (reassessment yrs only)' },
  TN: { start: { m: 5, d: 1 },  end: { m: 5, d: 1 },  label: 'May 1' },
  TX: { start: { m: 4, d: 15 }, end: { m: 5, d: 15 }, label: 'Apr 15 – May 15' },
  VA: { start: { m: 3, d: 1 },  end: { m: 3, d: 1 },  label: 'Mar 1' },
  WA: { start: { m: 6, d: 1 },  end: { m: 6, d: 1 },  label: 'Jun 1' },
  WV: { start: { m: 1, d: 1 },  end: { m: 1, d: 1 },  label: 'Jan 1' },
};

// 11 CA counties that statutorily mail individual notices. Everywhere
// else in CA, no notice is mailed — staff must retrieve from the roll.
// The eleven CA counties that proactively mail value notices. ⛔ KEYED ON assessors.name — the exact string
// this report now LABELS rows with, since it groups by assessor_ref_id and resolves the name from the
// assessors entity. Previously keyed on parcels.assessor free text, which is why it had bare county names.
//
// WHY NOT county or entity_id: assessors.county is NULL on all 58 CA rows (see the note in
// docs/rls-assessment.md §13.5 — a real column nobody populates), so county keying is unavailable; uuids
// would be opaque and would not survive a re-seed. CA assessor names are perfectly regular
// ('<County> County Assessor'), so an exact name match is the least fragile option actually available.
//
// ⛔ A NAME THAT MATCHES NOTHING FAILS SILENTLY — it returns "no proactive mailing", indistinguishable from a
// county that genuinely does not mail. Verify every entry against a real CA assessor row before trusting
// this Set; the query is in the commit message. Do NOT add an entry without checking it resolves.
const CA_NOTICE_COUNTIES = new Set([
  'Alameda County Assessor','Alpine County Assessor','Inyo County Assessor','Kings County Assessor',
  'Mono County Assessor','Placer County Assessor','San Francisco County Assessor',
  'San Luis Obispo County Assessor','Santa Clara County Assessor','Sierra County Assessor',
  'Ventura County Assessor',
]);

// Compute mailing-window status for an assessor row.
// Returns: { tier, daysLate, label, expected }
//   tier ∈ 'no_window' | 'not_yet_due' | 'in_window' | 'late' | 'severely_late'
//   daysLate is positive when past end of window, negative when not yet started
function computeNoticeStatus(state, assessor, today = new Date()) {
  // CA: only 11 counties have proactive notices
  if (state === 'CA') {
    if (!CA_NOTICE_COUNTIES.has(assessor || '')) {
      return { tier: 'no_window', daysLate: null, label: 'N/A', expected: 'No proactive mailing' };
    }
    return _computeAgainstDate(today, today.getFullYear(), 6, 1, 6, 1, 'Jul 1');
  }
  // PA: Allegheny does NOT proactively mail; rest get Jul 1
  if (state === 'PA') {
    if ((assessor || '').toLowerCase().includes('allegheny')) {
      return { tier: 'no_window', daysLate: null, label: 'N/A', expected: 'No proactive mailing' };
    }
    return _computeAgainstDate(today, today.getFullYear(), 6, 1, 6, 1, 'Jul 1 (most counties)');
  }
  // Standard window lookup
  const w = NOTICE_WINDOWS[(state || '').toUpperCase()];
  if (!w) return { tier: 'no_window', daysLate: null, label: 'N/A', expected: '—' };
  return _computeAgainstDate(today, today.getFullYear(),
    w.start.m - 1, w.start.d, w.end.m - 1, w.end.d, w.label, w.wraps_year);
}

// Internal helper for computeNoticeStatus.
function _computeAgainstDate(today, year, sm, sd, em, ed, label, wrapsYear) {
  const startDate = new Date(year, sm, sd);
  let endDate = new Date(year, em, ed);
  if (wrapsYear && endDate < startDate) endDate = new Date(year + 1, em, ed);
  const todayMid = new Date(year, today.getMonth(), today.getDate());
  const msPerDay = 86400000;

  if (todayMid < startDate) {
    const days = Math.round((startDate - todayMid) / msPerDay);
    return { tier: 'not_yet_due', daysLate: -days, label: `${days}d`, expected: label };
  }
  if (todayMid <= endDate) {
    return { tier: 'in_window', daysLate: 0, label: 'In window', expected: label };
  }
  const daysLate = Math.round((todayMid - endDate) / msPerDay);
  return {
    tier: daysLate > 30 ? 'severely_late' : 'late',
    daysLate,
    label: `${daysLate}d late`,
    expected: label,
  };
}

export default function NoticeProgress({ darkMode, onDrillParcel, allClients = [] }) {
  const isDark = darkMode;
  // One source for every value-year picker (folder_year_config, 2021-2028 fallback) — replaces the local
  // ['2024'..'2027'] literal that made 2021 unselectable.
  const { years: YEARS } = useYearRange();
  const c = isDark ? {
    bg: 'var(--ink)', card: 'rgba(47,56,84,0.6)', border: '#374060',
    text: '#e8eaf2', muted: 'var(--slate)', sub: '#a8b0cc',
    input: 'var(--ink)', inputBorder: '#434C68', gold: 'var(--red)',
    th: 'rgba(26,34,56,0.5)', rowHover: 'rgba(255,255,255,0.04)',
  } : {
    bg: 'var(--bg)', card: '#ffffff', border: 'var(--line)',
    text: 'var(--ink)', muted: 'var(--slate)', sub: '#4a5068',
    input: '#ffffff', inputBorder: '#c8ccd8', gold: 'var(--red-dark)',
    th: 'var(--line-soft)', rowHover: 'rgba(0,0,0,0.02)',
  };

  const s = {
    card: { background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', overflow: 'hidden', marginBottom: '16px', boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)' },
    cardH: { padding: '12px 18px', borderBottom: `1px solid ${c.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    cardT: { fontSize: '14px', fontWeight: '700', color: c.text },
    badge: (col) => ({ background: `rgba(${col},0.12)`, color: `rgb(${col})`, border: `1px solid rgba(${col},0.25)`, borderRadius: '20px', padding: '2px 9px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }),
    th: { padding: '9px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: c.muted, background: c.th, borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap', userSelect: 'none' },
    td: { padding: '10px 16px', fontSize: '12px', color: c.sub, borderBottom: `1px solid ${isDark ? 'rgba(67,76,104,0.25)' : '#eef0f6'}` },
  };

  // The current year — it was a hardcoded '2026' (step 8 plan §5).
  const [noticeYear, setNoticeYear] = useState(() => String(new Date().getFullYear()));
  // Client pick: '' = every client (the old page's only view, and still the default); 'c:<id>' a client; 'g:<id>' a
  // group, which stands for its clients at run time (utils/clientGroups).
  const [clientPick, setClientPick] = useState('');
  const [groups, setGroups] = useState([]);
  useEffect(() => {
    loadClientGroups().then((r) => { if (r.ok) setGroups(r.data.filter((g) => g.active)); else console.warn('[notice progress]', r.error); });
  }, []);
  const pickedClients = clientPick.startsWith('c:') ? [clientPick.slice(2)]
    : clientPick.startsWith('g:') ? resolveClientIds([], [clientPick.slice(2)], groups) : null;   // null = every client
  const pickedKey = pickedClients ? pickedClients.join(',') : '';
  const [noticeData, setNoticeData] = useState([]);
  const [noticeLoading, setNoticeLoading] = useState(true);
  const [noticeError, setNoticeError] = useState(null);
  const [noticeTotal, setNoticeTotal] = useState({ parcels: 0, received: 0, waiting: 0 });
  const [drillRow, setDrillRow] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillSearch, setDrillSearch] = useState('');
  const [assessorSearch, setAssessorSearch] = useState('');
  const [sortCol, setSortCol] = useState('days_late');
  const [sortDir, setSortDir] = useState('desc');
  const [drillSortCol, setDrillSortCol] = useState('parcel_name');
  const [drillSortDir, setDrillSortDir] = useState('asc');
  const drillRef = React.useRef(null);

  const loadNotices = useCallback(async () => {
    setNoticeLoading(true);
    let allParcels = [];
    let from = 0;
    while (true) {
      const { data: page } = await supabase.from('parcels')
        .select('id, client_id, assessor, assessor_ref_id, account_number, parcel_name, master_property_id, property_id, state, first_year, retired_year, reactivated_year')
        // ⛔ FILTER ON THE REF, not the text. The text filter dropped any parcel whose legacy parcels.assessor
        // was blank even when it carried a valid assessor_ref_id — silently absent from the totals, neither
        // waiting nor received. The ref is what the report now groups by, so it is what defines the universe.
        .not('assessor_ref_id', 'is', null)
        .range(from, from + 999);
      if (!page || page.length === 0) break;
      allParcels = [...allParcels, ...page];
      if (page.length < 1000) break;
      from += 1000;
    }

    // ── ⛔ REPRESENTATION GATE — the reason 2021 showed 35 parcels when only 30 were ours ───────────────────
    // This report had NO year test of any kind. Every other consumer of the representation rule
    // (expected_bills, pull_parcels_by_state, both rolls, parcel_states, parcel_states_summary) gates on
    // is_represented; this one counted every parcel in every year, so LaSalle's import — which brings in all
    // parcels regardless of when each came on board — put 5 parcels into 2021 that we did not have, and they
    // showed as WAITING, i.e. as work we owed.
    //
    // Gated on the SAME rule via the client mirror: represented from first_year, up to retired_year, then
    // again from reactivated_year, cascaded parcel AND property.
    //
    // ⛔ Joined on property_id, NOT master_property_id. Every SQL caller does
    // `join public.properties pr on pr.id = p.property_id`, and the drill-down's propByMpid map exists for a
    // different purpose (labelling). Using the same link as the RPCs is what stops this screen disagreeing
    // with Tax Bill Processing about the same parcel.
    //
    // ⛔ FAIL OPEN: a property_id that resolves to no properties row counts as REPRESENTED, consistent with
    // every other caller — a mapping gap must not silently delete a parcel from the totals. But it is LOGGED,
    // once per id, so a mis-mapped parcel is findable rather than quietly counted.
    const propRep = new Map();
    for (let from = 0; ; from += 1000) {
      const { data: props, error: propErr } = await supabase.from('properties')
        .select('id, client_id, first_year, retired_year, reactivated_year').range(from, from + 999);
      if (propErr) { console.warn('[notice tracking] properties read failed:', propErr.message); break; }
      (props || []).forEach(pr => propRep.set(pr.id, pr));
      if (!props || props.length < 1000) break;
    }
    // The Client pick: a parcel's client is its own client_id, else its property's.
    if (pickedClients) {
      const want = new Set(pickedClients);
      allParcels = allParcels.filter(p => want.has(p.client_id || (p.property_id && propRep.get(p.property_id)?.client_id)));
    }
    const unresolvedProps = new Set();
    const beforeGate = allParcels.length;
    allParcels = allParcels.filter(p => {
      const pr = p.property_id ? propRep.get(p.property_id) : null;
      if (p.property_id && !pr && !unresolvedProps.has(p.property_id)) {
        unresolvedProps.add(p.property_id);
        console.warn('[notice tracking] property_id resolves to no properties row:', p.property_id,
          '— parcel', p.account_number || p.id, 'counted as REPRESENTED (fail-open)');
      }
      return representedInYear(p, pr, noticeYear);
    });
    if (beforeGate !== allParcels.length) {
      console.info(`[notice tracking] ${beforeGate - allParcels.length} parcel(s) excluded from ${noticeYear} — not represented that year`);
    }

    // ⛔ "RECEIVED" = SCANNED (a parcel_values row) OR A NOTICE DOCUMENT FILED for the parcel and year
    // (utils/noticeReceived). It was parcel_values only, and this comment named the day that would go wrong: "if
    // notices that never pass through the scanner become normal, this under-counts". "File without scanning"
    // (2026-10-08) is exactly that — a notice filed in Notices with deliberately no values — and it would have sat
    // in WAITING for ever. Still not a union with parcel_appeals (the appeal record is not evidence of a notice).
    // Received-but-not-scanned parcels are marked in the drill-down; Notice Audit still lists scans only.
    // A read error fails the whole report LOUDLY rather than under-counting "received" in silence.
    let receipts;
    try { receipts = await loadNoticeReceipts(noticeYear); }
    catch (e) {
      console.error('[notice tracking] received read failed:', e.message);
      setNoticeError(`Couldn't work out which notices were received (${e.message}). The report is not shown rather than shown wrong.`);
      setNoticeData([]); setNoticeTotal({ parcels: 0, received: 0, waiting: 0 }); setNoticeLoading(false);
      return;
    }
    setNoticeError(null);

    // ⛔ GROUP BY THE ENTITY, LABEL FROM THE ENTITY. This keyed on `${p.assessor}||${p.state}` — the free-text
    // parcels.assessor — so one assessor entity split into two rows the moment two parcels spelled it
    // differently: 34 said 'Los Angeles County' and 1 said 'Los Angeles County Assessor', both pointing at the
    // SAME assessor_ref_id, which the report never read. Normalising the text fixes it once; grouping by the
    // ref fixes it permanently, because ParcelModal:2566 is a free-text box that can desync the name again on
    // any edit (the picker at :2544 sets both, that input sets only the text).
    const assessorById = await resolveVersionsBulk('assessors', allParcels.map(p => p.assessor_ref_id), noticeYear)
      .catch(e => { console.warn('[notice tracking] assessor resolve failed:', e.message); return new Map(); });
    const map = {};
    const warnedRefs = new Set();   // one warning per unresolved ref, not one per parcel
    allParcels.forEach(p => {
      const a = assessorById.get(p.assessor_ref_id);
      // ⛔ A REF THAT RESOLVES TO NOTHING keeps the parcel visible under its legacy text rather than vanishing
      // or collapsing into a blank row — pre-rebuild assessor_ref_ids that match no assessors row are a known
      // data gap. This fallback cannot re-create the split above: those parcels share a RESOLVED ref and now
      // group by it. Warn once per unresolved ref so the gap is detectable rather than merely survivable.
      if (!a && p.assessor_ref_id && !warnedRefs.has(p.assessor_ref_id)) {
        warnedRefs.add(p.assessor_ref_id);
        console.warn('[notice tracking] assessor_ref_id resolves to no assessors row:', p.assessor_ref_id, '— labelling from parcels.assessor');
      }
      const label = a?.name || p.assessor || '(unknown assessor)';
      const st = a?.state || p.state || '';
      const key = p.assessor_ref_id || `text:${label}||${st}`;
      if (!map[key]) map[key] = { assessor: label, state: st, received: [], waiting: [] };
      const got = noticeReceipt(receipts, p.id);
      if (got) map[key].received.push(got === 'filed' ? { ...p, notScanned: true } : p);
      else map[key].waiting.push(p);
    });

    const rows = Object.values(map).map(d => ({
      assessor: d.assessor, state: d.state,
      total: d.received.length + d.waiting.length,
      received: d.received.length, waiting: d.waiting.length,
      pct: Math.round((d.received.length / (d.received.length + d.waiting.length)) * 100),
      receivedParcels: d.received, waitingParcels: d.waiting,
    }));

    const totals = rows.reduce((acc, r) => ({ parcels: acc.parcels + r.total, received: acc.received + r.received, waiting: acc.waiting + r.waiting }), { parcels: 0, received: 0, waiting: 0 });
    setNoticeData(rows);
    setNoticeTotal(totals);
    setNoticeLoading(false);
  }, [noticeYear, pickedKey]);

  useEffect(() => { loadNotices(); }, [loadNotices]);

  function handleSort(col) {
    if (col === sortCol) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  }

  function sortIcon(col) {
    if (col !== sortCol) return <span style={{ color: '#4a5068', marginLeft: '4px' }}>⇅</span>;
    return <span style={{ color: 'var(--red)', marginLeft: '4px' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  }

  function handleDrillSort(col) {
    if (col === drillSortCol) setDrillSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setDrillSortCol(col); setDrillSortDir('asc'); }
  }

  function drillSortIcon(col) {
    if (col !== drillSortCol) return <span style={{ color: '#4a5068', marginLeft: '4px' }}>⇅</span>;
    return <span style={{ color: 'var(--red)', marginLeft: '4px' }}>{drillSortDir === 'asc' ? '↑' : '↓'}</span>;
  }

  const filtered = noticeData.filter(r =>
    !assessorSearch ||
    r.assessor.toLowerCase().includes(assessorSearch.toLowerCase()) ||
    (r.state || '').toLowerCase().includes(assessorSearch.toLowerCase())
  );

  const sortedData = [...filtered].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortCol === 'assessor') return dir * a.assessor.localeCompare(b.assessor);
    if (sortCol === 'state') return dir * (a.state || '').localeCompare(b.state || '');
    if (sortCol === 'total') return dir * (a.total - b.total);
    if (sortCol === 'received') return dir * (a.received - b.received);
    if (sortCol === 'waiting') return dir * (a.waiting - b.waiting);
    if (sortCol === 'pct') return dir * (a.pct - b.pct);
    if (sortCol === 'days_late') {
      // null daysLate (no_window assessors) always sort to the bottom
      const av = computeNoticeStatus(a.state, a.assessor).daysLate;
      const bv = computeNoticeStatus(b.state, b.assessor).daysLate;
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return dir * (av - bv);
    }
    return 0;
  });

  async function drillInto(row, mode) {
    setDrillLoading(true);
    setDrillSearch('');
    const parcels = mode === 'waiting' ? row.waitingParcels : row.receivedParcels;
    const mpids = [...new Set(parcels.map(p => p.master_property_id).filter(Boolean))];
    let properties = [];
    if (mpids.length > 0) {
      const { data } = await supabase.from('properties').select('id, name, master_property_id, client_id').in('master_property_id', mpids);
      properties = data || [];
    }
    const clientIds = [...new Set(properties.map(p => p.client_id).filter(Boolean))];
    let clients = [];
    if (clientIds.length > 0) {
      const { data } = await supabase.from('clients').select('id, name').in('id', clientIds);
      clients = data || [];
    }
    const parcelIds = parcels.map(p => p.id);
    // ⛔ CONSULTANT = THE FIRM ON consultant_assignments, resolved the way the parcel screen resolves it:
    // the latest row with value_year <= the viewed year (resolveFirmAssignment / ParcelFirmAssignment, 6d48353).
    // Same rule, same source, one implementation — a report that resolved this differently would disagree with
    // the parcel screen on exactly the rows someone would open to check it.
    //
    // WAS: parcel_contacts, .eq('role','consultant'). That table was DEPRECATED on 2026-07-30 and its own
    // comment names this caller — "still read by Reports.js:244 … drop in a follow-up after that is rewired."
    // It has been empty throughout, so every Consultant cell fell to the '—' default and looked like "nobody
    // assigned" rather than "reading a dead table". This is that follow-up: parcel_contacts now has no caller.
    //
    // ⛔ Resolution, NOT .eq(year): all 35 assignments are backfilled at 2021, so an exact-year query on 2026
    // returns nothing — identical on screen to the empty column this replaces.
    let firmByParcel = new Map();
    try {
      firmByParcel = await resolveFirmAssignmentsBulk(parcelIds, noticeYear);
    } catch (e) {
      console.warn('[notice tracking] consultant resolve failed:', e.message);   // degrade to '—', never blank the report
    }
    const propByMpid = {};
    properties.forEach(p => { propByMpid[p.master_property_id] = p; });
    const clientById = {};
    clients.forEach(cl => { clientById[cl.id] = cl; });

    const enriched = parcels.map(p => {
      const prop = propByMpid[p.master_property_id];
      const client = prop ? clientById[prop.client_id] : null;
      return { ...p, property_name: prop?.name || '—', client_name: client?.name || '—', consultant: firmByParcel.get(p.id)?.company || '—' };
    });
    setDrillRow({ assessor: row.assessor, state: row.state, mode, parcels: enriched });
    setDrillLoading(false);
    setTimeout(() => drillRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  }

  const filteredDrill = drillRow ? drillRow.parcels.filter(p => {
    if (!drillSearch) return true;
    const q = drillSearch.toLowerCase();
    return (p.parcel_name || '').toLowerCase().includes(q) || (p.account_number || '').toLowerCase().includes(q) ||
      (p.property_name || '').toLowerCase().includes(q) || (p.client_name || '').toLowerCase().includes(q) ||
      (p.consultant || '').toLowerCase().includes(q);
  }).sort((a, b) => {
    const dir = drillSortDir === 'asc' ? 1 : -1;
    const av = (a[drillSortCol] || '').toString().toLowerCase();
    const bv = (b[drillSortCol] || '').toString().toLowerCase();
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  }) : [];

  const overallPct = noticeTotal.parcels > 0 ? Math.round((noticeTotal.received / noticeTotal.parcels) * 100) : 0;

  // Excel: the By Assessor / County table as shown (search and sort applied), and the open drill-down list.
  const pickLabel = !clientPick ? 'All clients'
    : clientPick.startsWith('g:') ? (groups.find(g => g.id === clientPick.slice(2))?.name || 'Group')
    : (allClients.find(cl => cl.id === clientPick.slice(2))?.name || clientPick.slice(2));
  const exportTable = () => exportSheetsToXlsx(`Notice Progress ${noticeYear} - ${pickLabel}.xlsx`, [{
    name: `By Assessor ${noticeYear}`,
    titleRows: [`Notice Progress — ${noticeYear} — ${pickLabel}`],
    columns: [
      { key: 'assessor', label: 'Assessor / County' }, { key: 'state', label: 'State' },
      { key: 'total', label: 'Total', type: 'int' }, { key: 'received', label: 'Received', type: 'int' },
      { key: 'waiting', label: 'Waiting', type: 'int' }, { key: 'progress', label: 'Progress' },
      { key: 'expected', label: 'Expected By' }, { key: 'days_late', label: 'Days Late' }, { key: 'status', label: 'Status' },
    ],
    rows: sortedData.map(r => {
      const ns = computeNoticeStatus(r.state, r.assessor);
      return { assessor: r.assessor, state: r.state || '', total: r.total, received: r.received, waiting: r.waiting,
        progress: `${r.pct}%`, expected: ns.expected, days_late: ns.label,
        status: r.pct === 100 ? 'Complete' : r.waiting > 0 ? `${r.waiting} waiting` : 'In Progress' };
    }),
    totals: { assessor: 'Total', total: noticeTotal.parcels, received: noticeTotal.received, waiting: noticeTotal.waiting, progress: `${overallPct}%` },
  }]);
  const exportDrill = () => drillRow && exportSheetsToXlsx(
    `Notice Progress ${noticeYear} - ${drillRow.assessor} ${drillRow.mode === 'waiting' ? 'Waiting' : 'Received'}.xlsx`, [{
      name: drillRow.mode === 'waiting' ? 'Waiting' : 'Received',
      titleRows: [`${drillRow.assessor}${drillRow.state ? ` (${drillRow.state})` : ''} — ${drillRow.mode === 'waiting' ? 'Waiting' : 'Received'} — ${noticeYear}`],
      columns: [{ key: 'parcel_name', label: 'Parcel Name' }, { key: 'account_number', label: 'Account #' }, { key: 'property_name', label: 'Property' },
        { key: 'client_name', label: 'Client' }, { key: 'consultant', label: 'Consultant' }, { key: 'scanned', label: 'Scanned' }],
      rows: filteredDrill.map(p => ({ parcel_name: p.parcel_name || '', account_number: p.account_number || '', property_name: p.property_name,
        client_name: p.client_name, consultant: p.consultant, scanned: p.notScanned ? 'No — filed without scanning' : (drillRow.mode === 'waiting' ? '' : 'Yes') })),
    }]);
  const btn = { padding: '5px 12px', background: 'transparent', border: `1px solid ${c.border}`, borderRadius: '7px', color: c.muted, fontSize: '12px', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" };

  return (
    <div style={{ fontFamily: "'Public Sans',system-ui,sans-serif", color: c.text }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '22px' }}>
        <div style={{ fontSize: '13px', color: c.muted }}>Notice tracking by assessor</div>
        <select value={clientPick} onChange={e => { setClientPick(e.target.value); setDrillRow(null); }}
          style={{ padding: '5px 8px', fontSize: '13px', borderRadius: '8px', border: `1px solid ${c.inputBorder}`, background: c.input, color: c.text }}>
          <option value="">All clients</option>
          {groups.length > 0 && <optgroup label="Groups">{groups.map(g => <option key={g.id} value={`g:${g.id}`}>{g.name}</option>)}</optgroup>}
          <optgroup label="Clients">{allClients.map(cl => <option key={cl.id} value={`c:${cl.id}`}>{cl.name}</option>)}</optgroup>
        </select>
      </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Tax Year</div>
            {YEARS.map(y => (
              <button key={y} onClick={() => { setNoticeYear(y); setDrillRow(null); }} style={{
                padding: '5px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif",
                border: noticeYear === y ? '1px solid #B40000' : `1px solid ${c.border}`,
                background: noticeYear === y ? 'rgba(180,0,0,0.15)' : 'transparent',
                color: noticeYear === y ? 'var(--red)' : c.muted,
              }}>{y}</button>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', marginBottom: '16px' }}>
            {[
              { label: 'Total Parcels', value: noticeTotal.parcels, color: '180,0,0' },
              { label: 'Notices Received', value: noticeTotal.received, color: '52,211,153' },
              { label: 'Still Waiting', value: noticeTotal.waiting, color: '248,113,113' },
              { label: 'Complete', value: `${overallPct}%`, color: overallPct === 100 ? '52,211,153' : overallPct >= 50 ? '251,191,36' : '248,113,113' },
            ].map(kpi => (
              <div key={kpi.label} style={{ ...s.card, marginBottom: 0, padding: '16px 20px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '8px' }}>{kpi.label}</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: `rgb(${kpi.color})`, fontFamily: "'Archivo',sans-serif" }}>{noticeLoading ? '—' : kpi.value}</div>
              </div>
            ))}
          </div>

          {!noticeLoading && (
            <div style={{ ...s.card, padding: '14px 18px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: c.text }}>Overall Progress — {noticeYear}</div>
                <div style={{ fontSize: '12px', color: c.muted }}>{noticeTotal.received} of {noticeTotal.parcels} received</div>
              </div>
              <div style={{ height: '8px', background: isDark ? 'rgba(255,255,255,0.08)' : '#e8eaf2', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${overallPct}%`, background: overallPct === 100 ? '#34d399' : 'linear-gradient(90deg,#B40000,#34d399)', borderRadius: '4px' }} />
              </div>
            </div>
          )}

          <div ref={drillRef} />
          {drillRow && (
            <div style={{ ...s.card, marginBottom: '16px', border: `1px solid ${drillRow.mode === 'waiting' ? 'rgba(248,113,113,0.4)' : 'rgba(52,211,153,0.4)'}` }}>
              <div style={{ ...s.cardH, background: drillRow.mode === 'waiting' ? 'rgba(248,113,113,0.06)' : 'rgba(52,211,153,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button onClick={() => setDrillRow(null)} style={{ background: 'transparent', border: `1px solid ${c.border}`, borderRadius: '6px', color: c.muted, fontSize: '12px', padding: '4px 10px', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" }}>← Back</button>
                  <div style={s.cardT}>{drillRow.mode === 'waiting' ? '⏳' : '✓'} {drillRow.assessor} {drillRow.state ? `(${drillRow.state})` : ''} — {drillRow.mode === 'waiting' ? 'Waiting' : 'Received'}</div>
                  <span style={s.badge(drillRow.mode === 'waiting' ? '248,113,113' : '52,211,153')}>{filteredDrill.length} parcels</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button style={btn} onClick={exportDrill} disabled={drillLoading}>⤓ Excel</button>
                <input placeholder="Search parcels..." value={drillSearch} onChange={e => setDrillSearch(e.target.value)}
                  style={{ padding: '6px 12px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '12px', outline: 'none', width: '280px', fontFamily: "'Public Sans',system-ui,sans-serif" }} />
                </div>
              </div>
              {drillLoading ? (
                <div style={{ padding: '24px', textAlign: 'center', fontSize: '13px', color: c.muted }}>⏳ Loading...</div>
              ) : (
                <ScrollX style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr>
                        <th style={{ ...s.th, cursor: 'pointer' }} onClick={() => handleDrillSort('parcel_name')}>Parcel Name {drillSortIcon('parcel_name')}</th>
                        <th style={{ ...s.th, cursor: 'pointer' }} onClick={() => handleDrillSort('account_number')}>Account # {drillSortIcon('account_number')}</th>
                        <th style={{ ...s.th, cursor: 'pointer' }} onClick={() => handleDrillSort('property_name')}>Property {drillSortIcon('property_name')}</th>
                        <th style={{ ...s.th, cursor: 'pointer' }} onClick={() => handleDrillSort('client_name')}>Client {drillSortIcon('client_name')}</th>
                        <th style={{ ...s.th, cursor: 'pointer' }} onClick={() => handleDrillSort('consultant')}>Consultant {drillSortIcon('consultant')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDrill.map(p => (
                        <tr key={p.id} onClick={() => onDrillParcel && onDrillParcel(p)} style={{ cursor: onDrillParcel ? 'pointer' : 'default' }}
                          onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ ...s.td, fontWeight: '600', color: c.text }}>{p.parcel_name || '—'}
                            {p.notScanned && <span title="Notice filed without scanning — received, but no values were recorded (not in Notice Audit)"
                              style={{ marginLeft: '8px', fontSize: '10px', fontWeight: 500, color: c.muted, border: `1px solid ${c.border}`, borderRadius: '4px', padding: '1px 5px', whiteSpace: 'nowrap' }}>not scanned</span>}
                          </td>
                          <td style={{ ...s.td, fontFamily: "'DM Mono',monospace", fontSize: '11px' }}>{p.account_number || '—'}</td>
                          <td style={s.td}>{p.property_name}</td>
                          <td style={s.td}>{p.client_name}</td>
                          <td style={s.td}>{p.consultant}</td>
                        </tr>
                      ))}
                      {filteredDrill.length === 0 && <tr><td colSpan={5} style={{ ...s.td, textAlign: 'center', color: c.muted }}>No results</td></tr>}
                    </tbody>
                  </table>
                </ScrollX>
              )}
            </div>
          )}

          <div style={s.card}>
            <div style={s.cardH}>
              <div style={s.cardT}>📊 By Assessor / County — {noticeYear}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input placeholder="🔍 Search assessor or state..." value={assessorSearch} onChange={e => setAssessorSearch(e.target.value)}
                  style={{ padding: '5px 10px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '12px', outline: 'none', width: '220px', fontFamily: "'Public Sans',system-ui,sans-serif" }} />
                <span style={{ fontSize: '11px', color: c.muted }}>Click numbers to drill down</span>
                <span style={s.badge('180,0,0')}>{sortedData.length} assessors</span>
                <button style={btn} onClick={exportTable} disabled={noticeLoading || !!noticeError}>⤓ Excel</button>
              </div>
            </div>
            {noticeError ? (
              <div style={{ padding: '18px 24px', fontSize: '13px', color: '#f87171', fontWeight: 600 }}>⚠ {noticeError}</div>
            ) : noticeLoading ? (
              <div style={{ padding: '24px', textAlign: 'center', fontSize: '13px', color: c.muted }}>⏳ Loading...</div>
            ) : (
              <ScrollX style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...s.th, cursor: 'pointer' }} onClick={() => handleSort('assessor')}>Assessor / County {sortIcon('assessor')}</th>
                      <th style={{ ...s.th, cursor: 'pointer' }} onClick={() => handleSort('state')}>State {sortIcon('state')}</th>
                      <th style={{ ...s.th, textAlign: 'right', cursor: 'pointer' }} onClick={() => handleSort('total')}>Total {sortIcon('total')}</th>
                      <th style={{ ...s.th, textAlign: 'right', cursor: 'pointer' }} onClick={() => handleSort('received')}>Received {sortIcon('received')}</th>
                      <th style={{ ...s.th, textAlign: 'right', cursor: 'pointer' }} onClick={() => handleSort('waiting')}>Waiting {sortIcon('waiting')}</th>
                      <th style={{ ...s.th, textAlign: 'center', cursor: 'pointer' }} onClick={() => handleSort('pct')}>Progress {sortIcon('pct')}</th>
                      <th style={{ ...s.th, textAlign: 'center' }}>Expected By</th>
                      <th style={{ ...s.th, textAlign: 'center', cursor: 'pointer' }} onClick={() => handleSort('days_late')}>Days Late {sortIcon('days_late')}</th>
                      <th style={{ ...s.th, textAlign: 'center' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedData.map((row, idx) => {
                      const ns = computeNoticeStatus(row.state, row.assessor);
                      const tierColor = {
                        no_window: '107,116,153',
                        not_yet_due: '107,116,153',
                        in_window: '251,191,36',
                        late: '249,115,22',
                        severely_late: '248,113,113',
                      }[ns.tier];
                      return (
                        <tr key={`${row.assessor}-${row.state}-${idx}`}
                          onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
                          onMouseLeave={e => e.currentTarget.style.background = drillRow?.assessor === row.assessor && drillRow?.state === row.state ? (isDark ? 'rgba(180,0,0,0.06)' : 'rgba(180,0,0,0.04)') : 'transparent'}
                          style={{ background: drillRow?.assessor === row.assessor && drillRow?.state === row.state ? (isDark ? 'rgba(180,0,0,0.06)' : 'rgba(180,0,0,0.04)') : 'transparent' }}>
                          <td style={{ ...s.td, fontWeight: '600', color: c.text }}>{row.assessor}</td>
                          <td style={{ ...s.td, fontSize: '11px', fontFamily: "'DM Mono',monospace", color: c.muted }}>{row.state || '—'}</td>
                          <td style={{ ...s.td, textAlign: 'right', fontFamily: "'DM Mono',monospace" }}>{row.total}</td>
                          <td style={{ ...s.td, textAlign: 'right' }}>
                            <span onClick={() => row.received > 0 && drillInto(row, 'received')}
                              style={{ color: '#34d399', fontWeight: '700', cursor: row.received > 0 ? 'pointer' : 'default', fontFamily: "'DM Mono',monospace", textDecoration: row.received > 0 ? 'underline' : 'none', textUnderlineOffset: '3px' }}>
                              {row.received}
                            </span>
                          </td>
                          <td style={{ ...s.td, textAlign: 'right' }}>
                            <span onClick={() => row.waiting > 0 && drillInto(row, 'waiting')}
                              style={{ color: row.waiting > 0 ? '#f87171' : '#34d399', fontWeight: '700', cursor: row.waiting > 0 ? 'pointer' : 'default', fontFamily: "'DM Mono',monospace", textDecoration: row.waiting > 0 ? 'underline' : 'none', textUnderlineOffset: '3px' }}>
                              {row.waiting}
                            </span>
                          </td>
                          <td style={{ ...s.td, textAlign: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                              <div style={{ width: '80px', height: '6px', background: isDark ? 'rgba(255,255,255,0.08)' : '#e8eaf2', borderRadius: '3px', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${row.pct}%`, background: row.pct === 100 ? '#34d399' : row.pct >= 50 ? '#fbbf24' : '#f87171', borderRadius: '3px' }} />
                              </div>
                              <span style={{ fontSize: '11px', color: c.muted, minWidth: '32px' }}>{row.pct}%</span>
                            </div>
                          </td>
                          <td style={{ ...s.td, textAlign: 'center', fontSize: '11px', color: c.muted }}>{ns.expected}</td>
                          <td style={{ ...s.td, textAlign: 'center' }}>
                            <span style={s.badge(tierColor)}>{ns.label}</span>
                          </td>
                          <td style={{ ...s.td, textAlign: 'center' }}>
                            {row.pct === 100 ? <span style={s.badge('52,211,153')}>✓ Complete</span>
                              : row.waiting > 0 ? <span style={s.badge('248,113,113')}>⏳ {row.waiting} waiting</span>
                              : <span style={s.badge('251,191,36')}>In Progress</span>}
                          </td>
                        </tr>
                      );
                    })}
                    {sortedData.length === 0 && <tr><td colSpan={9} style={{ ...s.td, textAlign: 'center', color: c.muted }}>{assessorSearch ? 'No assessors match' : `No data for ${noticeYear}`}</td></tr>}
                  </tbody>
                </table>
              </ScrollX>
            )}
          </div>
    </div>
  );
}
